import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Row, Col, Tabs, Input, Button, Tag, Empty, Space, Tooltip, Badge, Pagination, Alert } from 'antd';
import {
  ReloadOutlined,
  SendOutlined,
  CustomerServiceOutlined,
  RobotOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { supportChatApi } from '../../../api/support-chat.api.js';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { toast } from '../../../utils/toast.js';

// Chat hỗ trợ khách hàng — chỉ ADMIN. Xem lịch sử khung chat góc phải website (lưu 15 ngày kể từ tin cuối),
// chiếm quyền chat để trả lời trực tiếp (BOT ngừng tự trả lời) và trả lại cho BOT khi xong.
// API: /support-chat/admin/conversations...

const LIST_POLL_MS = 10000;
const MESSAGES_POLL_MS = 4000;
const PAGE_SIZE = 20;

const fmtTime = (v) => (v ? new Date(v).toLocaleString('vi-VN', { hour12: false, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');

const customerName = (c) => c.customer?.fullName || `Khách vãng lai #${c.guestCode}`;

const SENDER_STYLE = {
  USER: { align: 'flex-start', bg: '#ffffff', border: '#e2e8f0', label: 'Khách' },
  BOT: { align: 'flex-end', bg: '#f6f8f5', border: '#dfe6e1', label: 'BOT' },
  AGENT: { align: 'flex-end', bg: '#e3f1e8', border: '#b9dcc8', label: 'Tư vấn viên' },
};

const actionLabel = (a) => (typeof a === 'string' ? a : a.label);

// onWaitingChange: báo số cuộc chat đang chờ lên thanh bên trang quản trị
export function AdminSupportChatPanel({ onWaitingChange }) {
  const { user } = useAuth();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({ total: 0, waiting: 0, human: 0, retentionDays: null });
  const [listLoading, setListLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null); // { conversation, messages }
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);
  const detailRef = useRef(null);
  detailRef.current = detail;

  const loadList = useCallback(
    async (showLoading = false) => {
      if (showLoading) setListLoading(true);
      try {
        const res = await supportChatApi.adminListConversations({ filter, search: search || undefined, page, pageSize: PAGE_SIZE });
        setList(res.data || []);
        setMeta(res.meta || {});
      } catch (err) {
        if (showLoading) toast.error(err.message || 'Không tải được danh sách cuộc trò chuyện');
      } finally {
        if (showLoading) setListLoading(false);
      }
    },
    [filter, search, page]
  );

  useEffect(() => {
    onWaitingChange?.(meta.waiting || 0);
  }, [meta.waiting, onWaitingChange]);

  useEffect(() => {
    loadList(true);
    const timer = setInterval(() => !document.hidden && loadList(false), LIST_POLL_MS);
    return () => clearInterval(timer);
  }, [loadList]);

  const appendMessages = useCallback((data) => {
    setDetail((prev) => {
      if (!prev || prev.conversation.id !== data.conversation.id) return prev;
      const known = new Set(prev.messages.map((m) => m.id));
      return { conversation: data.conversation, messages: [...prev.messages, ...data.messages.filter((m) => !known.has(m.id))] };
    });
  }, []);

  // Mở một cuộc trò chuyện, sau đó hỏi tin mới định kỳ
  useEffect(() => {
    if (!selectedId) return undefined;
    let cancelled = false;
    setDetail(null);
    supportChatApi
      .adminGetMessages(selectedId)
      .then((res) => !cancelled && setDetail(res.data))
      .catch((err) => toast.error(err.message || 'Không tải được cuộc trò chuyện'));
    const timer = setInterval(async () => {
      const current = detailRef.current;
      if (document.hidden || !current || current.conversation.id !== selectedId) return;
      const afterId = current.messages.reduce((max, m) => Math.max(max, m.id), 0);
      try {
        const res = await supportChatApi.adminGetMessages(selectedId, afterId);
        if (!cancelled) appendMessages(res.data);
      } catch {
        // lượt sau hỏi lại
      }
    }, MESSAGES_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [selectedId, appendMessages]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [detail?.messages.length]);

  const runAction = async (fn, successMsg) => {
    if (!detail || busy) return;
    setBusy(true);
    try {
      const res = await fn(detail.conversation.id);
      appendMessages(res.data);
      if (successMsg) toast.success(successMsg);
      loadList(false);
      return true;
    } catch (err) {
      toast.error(err.message || 'Thao tác không thành công');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    const value = text.trim();
    if (!value) return;
    const ok = await runAction((id) => supportChatApi.adminSendMessage(id, value));
    if (ok) setText('');
  };

  const conv = detail?.conversation;
  const isMine = conv?.mode === 'HUMAN' && conv.agent?.id === user?.id;

  const tabLabel = (label, count) => (
    <span>
      {label} {count ? <Badge count={count} size="small" style={{ marginLeft: 4 }} /> : null}
    </span>
  );

  return (
    <section className="adm-panel">
      <div className="adm-panel-bar">
        <h2>Cuộc trò chuyện</h2>
        <Tooltip title="Tải lại">
          <Button icon={<ReloadOutlined />} aria-label="Tải lại danh sách" onClick={() => loadList(true)} loading={listLoading} />
        </Tooltip>
      </div>
      <div className="adm-panel-pad">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={`Lịch sử lưu ${meta.retentionDays || 15} ngày kể từ tin cuối. Bấm "Chiếm quyền chat" (hoặc gửi tin) để trả lời trực tiếp — BOT ngừng tự trả lời cho tới khi bạn bấm "Trả lại cho BOT" hoặc im lặng quá thời gian cấu hình.`}
      />
      <Row gutter={16}>
        <Col xs={24} lg={9}>
          <Tabs
            size="small"
            activeKey={filter}
            onChange={(k) => {
              setFilter(k);
              setPage(1);
            }}
            items={[
              { key: 'all', label: 'Tất cả' },
              { key: 'waiting', label: tabLabel('Đang chờ', meta.waiting) },
              { key: 'human', label: tabLabel('Người đang chat', meta.human) },
              { key: 'mine', label: 'Của tôi' },
            ]}
          />
          <Input.Search
            allowClear
            placeholder="Tìm tên, email, SĐT, nội dung…"
            onSearch={(v) => {
              setSearch(v.trim());
              setPage(1);
            }}
            style={{ marginBottom: 8 }}
          />
          <div style={{ maxHeight: 560, overflowY: 'auto', border: '1px solid #dfe6e1', borderRadius: 8 }}>
            {list.length === 0 ? (
              <Empty style={{ padding: 24 }} description="Chưa có cuộc trò chuyện" />
            ) : (
              list.map((c) => (
                <div
                  key={c.id}
                  role="button"
                  tabIndex={0}
                  aria-current={c.id === selectedId ? 'true' : undefined}
                  onClick={() => setSelectedId(c.id)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setSelectedId(c.id))}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #dfe6e1',
                    background: c.id === selectedId ? '#e3f1e8' : c.humanRequestedAt ? '#fdf8ef' : '#fff',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {customerName(c)}
                    </strong>
                    <span style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtTime(c.lastMessageAt)}</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.lastMessagePreview}
                  </div>
                  <Space size={4} style={{ marginTop: 4 }} wrap>
                    {c.mode === 'HUMAN' ? (
                      <Tag color="green" icon={<CustomerServiceOutlined />}>
                        {c.agent?.fullName || 'Tư vấn viên'}
                      </Tag>
                    ) : (
                      <Tag icon={<RobotOutlined />}>BOT</Tag>
                    )}
                    {c.humanRequestedAt && <Tag color="orange">Yêu cầu gặp người</Tag>}
                    {c.unreadByAgent > 0 && <Tag color="red">{c.unreadByAgent} tin mới</Tag>}
                  </Space>
                </div>
              ))
            )}
          </div>
          {meta.total > PAGE_SIZE && (
            <Pagination
              size="small"
              style={{ marginTop: 8, textAlign: 'right' }}
              current={page}
              pageSize={PAGE_SIZE}
              total={meta.total}
              onChange={setPage}
              showSizeChanger={false}
            />
          )}
        </Col>

        <Col xs={24} lg={15}>
          {!selectedId ? (
            <Empty style={{ padding: 48 }} description="Chọn một cuộc trò chuyện để xem" />
          ) : !detail ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>Đang tải…</div>
          ) : (
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, display: 'flex', flexDirection: 'column', height: 640 }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                  <div>
                    <strong>
                      <UserOutlined /> {customerName(conv)}
                    </strong>
                    {conv.customer && (
                      <div style={{ fontSize: 12.5, color: '#64748b' }}>
                        {conv.customer.phone && (
                          <a href={`tel:${conv.customer.phone}`} style={{ marginRight: 10 }}>
                            <PhoneOutlined /> {conv.customer.phone}
                          </a>
                        )}
                        <MailOutlined /> {conv.customer.email}
                        {conv.customer.pharmacyName ? ` · ${conv.customer.pharmacyName}` : ''}
                      </div>
                    )}
                    {conv.lastPagePath && <div style={{ fontSize: 12, color: '#94a3b8' }}>Đang xem: {conv.lastPagePath}</div>}
                  </div>
                  <Space>
                    {conv.mode === 'HUMAN' ? (
                      <Tag color="green">{isMine ? 'Bạn đang chat' : `${conv.agent?.fullName || 'Tư vấn viên'} đang chat`}</Tag>
                    ) : (
                      <Tag>BOT đang trả lời</Tag>
                    )}
                    {!isMine && (
                      <Button type="primary" loading={busy} onClick={() => runAction(supportChatApi.adminTakeover, 'Đã chiếm quyền chat')}>
                        Chiếm quyền chat
                      </Button>
                    )}
                    {conv.mode === 'HUMAN' && (
                      <Button loading={busy} onClick={() => runAction(supportChatApi.adminRelease, 'Đã trả lại cho BOT')}>
                        Trả lại cho BOT
                      </Button>
                    )}
                  </Space>
                </div>
              </div>

              <div ref={bodyRef} style={{ flex: 1, overflowY: 'auto', padding: 12, background: '#f6f8f5', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {detail.messages.map((m) =>
                  m.sender === 'SYSTEM' ? (
                    <div key={m.id} style={{ alignSelf: 'center', fontSize: 12, color: '#64748b', background: '#e2e8f0', borderRadius: 999, padding: '2px 12px' }}>
                      {m.text} · {fmtTime(m.createdAt)}
                    </div>
                  ) : (
                    <div key={m.id} style={{ alignSelf: SENDER_STYLE[m.sender].align, maxWidth: '80%' }}>
                      <div style={{ fontSize: 11.5, color: '#94a3b8', textAlign: SENDER_STYLE[m.sender].align === 'flex-end' ? 'right' : 'left' }}>
                        {m.sender === 'AGENT' ? m.agentName || 'Tư vấn viên' : SENDER_STYLE[m.sender].label} · {fmtTime(m.createdAt)}
                      </div>
                      <div
                        style={{
                          background: SENDER_STYLE[m.sender].bg,
                          border: `1px solid ${SENDER_STYLE[m.sender].border}`,
                          borderRadius: 10,
                          padding: '7px 11px',
                          whiteSpace: 'pre-line',
                          wordBreak: 'break-word',
                        }}
                      >
                        {m.text}
                      </div>
                      {m.actions.length > 0 && (
                        <div style={{ fontSize: 11.5, color: '#94a3b8' }}>Nút gợi ý: {m.actions.map(actionLabel).join(', ')}</div>
                      )}
                    </div>
                  )
                )}
              </div>

              <div style={{ padding: 10, borderTop: '1px solid #e2e8f0', display: 'flex', gap: 8 }}>
                <Input.TextArea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  maxLength={1500}
                  placeholder={isMine ? 'Nhập tin trả lời khách (Enter để gửi, Shift+Enter xuống dòng)' : 'Gửi tin sẽ tự chiếm quyền chat'}
                />
                <Button type="primary" icon={<SendOutlined />} onClick={handleSend} loading={busy} disabled={!text.trim()}>
                  Gửi
                </Button>
              </div>
            </div>
          )}
        </Col>
      </Row>
      </div>
    </section>
  );
}
